import { useEffect, useRef, useState } from "react";
import { Radio, Youtube, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

// Load YouTube IFrame API once, memoized
let _ytApiPromise = null;
function loadYouTubeAPI() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const first = document.getElementsByTagName("script")[0];
    first.parentNode.insertBefore(tag, first);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") { try { prev(); } catch (_) { /* noop */ } }
      resolve(window.YT);
    };
  });
  return _ytApiPromise;
}

export default function LiveBlock() {
  const [live, setLive] = useState(null);
  const [ended, setEnded] = useState(false);
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    let active = true;
    const load = () => {
      api.get("/live/status").then((r) => {
        if (!active) return;
        setLive((prev) => {
          const next = r.data;
          if (prev && prev.video_id === next?.video_id && prev.live === next?.live) return prev;
          setEnded(false);
          return next;
        });
      }).catch(() => {});
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { active = false; clearInterval(id); };
  }, []);

  // Build/replace YouTube Player when video_id changes
  const videoId = live?.video_id;
  useEffect(() => {
    if (!videoId || !containerRef.current) return;
    let cancelled = false;
    loadYouTubeAPI().then((YT) => {
      if (cancelled || !containerRef.current) return;
      // Destroy previous
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (_) { /* noop */ }
        playerRef.current = null;
      }
      // Clear container
      containerRef.current.innerHTML = "";
      const div = document.createElement("div");
      div.id = `yt-player-${videoId}`;
      div.style.width = "100%";
      div.style.height = "100%";
      containerRef.current.appendChild(div);
      playerRef.current = new YT.Player(div.id, {
        width: "100%",
        height: "100%",
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          fs: 1,
          controls: 1,
          disablekb: 1,
        },
        events: {
          onStateChange: (e) => {
            // 0 = ENDED. Prevent YouTube from suggesting/auto-playing other videos.
            if (e.data === 0) {
              try { e.target.stopVideo(); } catch (_) { /* noop */ }
              setEnded(true);
            }
          },
          onError: (e) => {
            // 100 = removed/private; 101/150 = embed disabled. Show overlay in those cases.
            if (e && (e.data === 100 || e.data === 101 || e.data === 150)) {
              setEnded(true);
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (_) { /* noop */ }
        playerRef.current = null;
      }
    };
  }, [videoId]);

  const hasReal = live?.configured && live?.video_id;
  const isLive = !!live?.live;
  const isUpcoming = !!live?.upcoming;

  // Format scheduled start nicely (es-BO locale, UTC-4 is auto-handled by toLocaleString)
  const scheduledLabel = (() => {
    if (!live?.scheduled_start) return null;
    try {
      const d = new Date(live.scheduled_start);
      return d.toLocaleString("es-BO", {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
    } catch (_) {
      return null;
    }
  })();

  return (
    <section
      className="rounded-sm overflow-hidden border"
      style={{ background: "var(--hero-bg)", color: "white", borderColor: "var(--border)" }}
      data-testid="live-block"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          {hasReal && isLive && !ended ? (
            <span className="px-2 py-1 bg-red-600 text-white text-[10px] font-bold uppercase rounded-sm flex items-center gap-1.5 flex-shrink-0">
              <span className="live-dot" /> EN VIVO
            </span>
          ) : hasReal && isUpcoming ? (
            <span className="px-2 py-1 bg-amber-500 text-black text-[10px] font-bold uppercase rounded-sm flex-shrink-0" data-testid="live-upcoming-badge">
              PRÓXIMAMENTE
            </span>
          ) : hasReal ? (
            <span className="px-2 py-1 bg-white/15 text-white text-[10px] font-bold uppercase rounded-sm flex-shrink-0">
              {ended ? "TERMINADO" : "ÚLTIMO VIDEO"}
            </span>
          ) : (
            <span className="px-2 py-1 bg-white/10 text-white text-[10px] font-bold uppercase rounded-sm flex items-center gap-1.5 flex-shrink-0">
              <Youtube size={11} /> POPU LIVE
            </span>
          )}
          <p className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1 flex-shrink-0">
            <Radio size={10} className="text-red-500" /> POPU.NEWS EN VIVO
          </p>
        </div>
        {hasReal && (
          <a
            href={live.watch_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/80 hover:text-white transition flex-shrink-0"
            data-testid="live-watch-youtube"
          >
            <ExternalLink size={11} /> Ver en YouTube
          </a>
        )}
      </div>

      <div className="relative aspect-video bg-black w-full">
        {hasReal ? (
          <>
            <div ref={containerRef} className="absolute inset-0 w-full h-full" />
            {ended && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/85 backdrop-blur-sm text-center p-6" data-testid="live-ended-overlay">
                <div>
                  <Youtube size={48} className="mx-auto mb-3 text-white/80" />
                  <p className="font-headline uppercase text-2xl leading-tight">Transmisión finalizada</p>
                  <p className="text-sm text-white/70 mt-2 max-w-xs mx-auto">
                    Esperá el próximo vivo o mirá los últimos videos en el canal.
                  </p>
                  <a
                    href={live.watch_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-white text-xs font-bold uppercase tracking-widest transition"
                  >
                    <Youtube size={14} /> Ir al canal
                  </a>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-center p-6">
            <div>
              <Youtube size={44} className="mx-auto mb-3 text-white/80" />
              <p className="font-headline uppercase text-lg leading-tight">POPU.news EN VIVO</p>
              <p className="text-xs text-white/70 mt-2 max-w-xs mx-auto">
                Configurá tu canal de YouTube desde el panel admin para transmitir en vivo.
              </p>
            </div>
          </div>
        )}
      </div>

      {hasReal && live.title && (
        <div className="px-4 py-3 border-t border-white/10">
          <p className="text-white font-headline text-base md:text-lg uppercase leading-tight line-clamp-2">
            {live.program && <span className="text-[#ec4899] mr-2">[{live.program}]</span>}
            {live.title}
          </p>
          {isUpcoming && scheduledLabel && (
            <p className="text-amber-400 text-xs uppercase tracking-widest font-bold mt-1" data-testid="live-scheduled-start">
              Comienza: {scheduledLabel}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
